import { CartAddEvent } from '@theme/events';

/**
 * Söta quick buy: a size picker and add-to-cart button outside the product page.
 * Enhances a native product form. Adds the chosen variant through the Ajax cart,
 * refreshes the cart sections and opens the cart drawer. Without JavaScript the
 * form posts normally and the shopper lands on the cart page.
 */
class SotaQuickBuy extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    this.querySelector('form')?.addEventListener('submit', this.#handleSubmit, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    const form = /** @type {HTMLFormElement} */ (event.currentTarget);
    const button = form.querySelector('button[type="submit"]');
    const label = form.querySelector('[data-label]');
    const variantId = Number(new FormData(form).get('id'));
    if (!(button instanceof HTMLButtonElement) || !variantId) return;

    event.preventDefault();

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    const defaultLabel = label?.textContent ?? '';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (label) label.textContent = this.dataset.addingText ?? defaultLabel;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const result = await response.json();

      if (result.status) {
        this.#announce(result.description || result.message || this.dataset.errorText || '');
        this.dispatchEvent(new CartAddEvent({}, this.id, { didError: true, source: 'sota-quick-buy' }));
        return;
      }

      this.#announce(this.dataset.addedText ?? '');
      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'sota-quick-buy',
          itemCount: 1,
          productId: this.dataset.productId,
          variantId: String(variantId),
          sections: result.sections,
        })
      );

      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && !drawer.hasAttribute('auto-open') && 'open' in drawer && typeof drawer.open === 'function') {
        drawer.open();
      }
    } catch (error) {
      console.error(error);
      form.submit();
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (label) label.textContent = defaultLabel;
    }
  };

  /** @param {string} message */
  #announce(message) {
    const status = this.querySelector('[data-status]');
    if (status) status.textContent = message;
  }
}

if (!customElements.get('sota-quick-buy')) {
  customElements.define('sota-quick-buy', SotaQuickBuy);
}
